import { useState, useCallback } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  type SortingState,
} from '@tanstack/react-table'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Search, Download, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '@/components/ui/sheet'
import { paymentsApi, type PaymentListItem, type PaymentDetail } from '@/lib/api/payments'

// ─── Status badge mapping ──────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, BadgeProps['variant']> = {
  DRAFT: 'secondary',
  PENDING_APPROVAL: 'pending',
  APPROVED: 'secondary',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILED: 'destructive',
  CANCELLED: 'secondary',
  REVERSED: 'warning',
  REJECTED: 'destructive',
}

// ─── Column helper ────────────────────────────────────────────────────────────

const col = createColumnHelper<PaymentListItem>()

const columns = [
  col.accessor('createdAt', {
    header: 'Date',
    cell: (info) =>
      new Date(info.getValue()).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
      }),
  }),
  col.accessor('payeeName', { header: 'Payee' }),
  col.accessor('paymentMode', { header: 'Mode' }),
  col.accessor('amount', {
    header: 'Amount',
    cell: (info) =>
      new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(
        info.getValue() / 100,
      ),
  }),
  col.accessor('status', {
    header: 'Status',
    cell: (info) => (
      <Badge variant={STATUS_BADGE[info.getValue()] ?? 'secondary'}>{info.getValue()}</Badge>
    ),
  }),
  col.accessor('externalReferenceId', {
    header: 'Reference',
    cell: (info) => <span className="font-mono text-xs">{info.getValue() ?? '—'}</span>,
  }),
]

// ─── Detail sheet ─────────────────────────────────────────────────────────────

function PaymentDetailSheet({ id }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['payment', id],
    queryFn: () => paymentsApi.getById(id),
    select: (res) => res.data as PaymentDetail,
  })

  const { data: statusData, refetch: refetchStatus } = useQuery({
    queryKey: ['payment-status', id],
    queryFn: () => paymentsApi.getStatus(id),
    select: (res) => res.data,
    enabled: !!data && ['PROCESSING', 'PENDING_APPROVAL'].includes(data.status),
    refetchInterval: false,
  })

  if (isLoading || !data) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const latestTxn = data.transactions?.[0]

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground">Payment Request</p>
          <p className="font-mono text-sm">{data.id}</p>
        </div>
        <Badge variant={STATUS_BADGE[data.status] ?? 'secondary'}>{data.status}</Badge>
      </div>

      <Section title="Payment Details">
        <DetailRow label="Payee" value={data.payeeName} />
        <DetailRow label="Mode" value={data.paymentMode} />
        <DetailRow label="Account" value={data.creditAccount} />
        <DetailRow label="IFSC" value={data.creditIfsc} />
        <DetailRow
          label="Amount"
          value={new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(data.amount / 100)}
          bold
        />
        {data.remarks && <DetailRow label="Remarks" value={data.remarks} />}
        <DetailRow label="Created by" value={data.creator?.name ?? '—'} />
        <DetailRow
          label="Created at"
          value={new Date(data.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
        />
      </Section>

      {data.vendor && (
        <Section title="Vendor">
          <DetailRow label="Name" value={data.vendor.name} />
        </Section>
      )}

      {latestTxn && (
        <Section title="ICICI Transaction">
          {latestTxn.iciciUtr && <DetailRow label="UTR" value={latestTxn.iciciUtr} mono />}
          {latestTxn.iciciStatus && <DetailRow label="ICICI Status" value={latestTxn.iciciStatus} />}
        </Section>
      )}

      {statusData && (
        <Section title="Live Status">
          <DetailRow label="Status" value={statusData.status} />
          {statusData.utr && <DetailRow label="UTR" value={statusData.utr} mono />}
          <Button size="sm" variant="outline" onClick={() => refetchStatus()}>
            <RefreshCw className="mr-1 h-3 w-3" /> Refresh
          </Button>
        </Section>
      )}

      {data.approvalLogs && data.approvalLogs.length > 0 && (
        <Section title="Approval History">
          {data.approvalLogs.map((log) => (
            <div key={log.id} className="flex justify-between text-sm">
              <span className="font-medium">{log.action}</span>
              <span className="text-muted-foreground">
                {log.actor?.name} · {new Date(log.createdAt).toLocaleDateString('en-IN')}
              </span>
            </div>
          ))}
        </Section>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      <div className="space-y-2 rounded-md border p-3">{children}</div>
    </div>
  )
}

function DetailRow({ label, value, bold, mono }: { label: string; value: string; bold?: boolean; mono?: boolean }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`${bold ? 'font-semibold' : ''} ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  )
}

// ─── Main Table ───────────────────────────────────────────────────────────────

export default function PaymentHistory() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [sorting, setSorting] = useState<SortingState>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const limit = 20

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['payments', page, search],
    queryFn: () => paymentsApi.list({ page, limit, search: search || undefined }),
    select: (res) => res.data,
  })

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: data ? Math.ceil(data.total / limit) : 0,
  })

  const exportCSV = useCallback(() => {
    if (!data?.items) return
    const headers = ['Date', 'Payee', 'Mode', 'Amount', 'Status', 'Reference']
    const rows = data.items.map((p) => [
      new Date(p.createdAt).toLocaleDateString('en-IN'),
      p.payeeName,
      p.paymentMode,
      (p.amount / 100).toFixed(2),
      p.status,
      p.externalReferenceId ?? '',
    ])
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }, [data])

  const totalPages = data ? Math.ceil(data.total / limit) : 1

  return (
    <>
      <div className="space-y-4">
        {/* Toolbar */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search payee or reference..."
              className="pl-8"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['payments'] })}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV}>
            <Download className="mr-1 h-4 w-4" /> CSV
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b bg-muted/50">
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-3 text-left font-medium text-muted-foreground cursor-pointer select-none"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span className="inline-flex items-center gap-1">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          header.column.getIsSorted() === 'asc' ? <ChevronUp className="h-3 w-3" /> :
                          header.column.getIsSorted() === 'desc' ? <ChevronDown className="h-3 w-3" /> :
                          <ChevronsUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="py-12 text-center text-muted-foreground">
                    <RefreshCw className="mx-auto mb-2 h-5 w-5 animate-spin" />
                    Loading...
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-12 text-center text-muted-foreground">
                    No payments found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b hover:bg-muted/30 cursor-pointer transition-colors"
                    onClick={() => setSelectedId(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.total > limit && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, data.total)} of {data.total}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selectedId} onOpenChange={(open) => { if (!open) setSelectedId(null) }}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Payment Details</SheetTitle>
            <SheetClose onClose={() => setSelectedId(null)} />
          </SheetHeader>
          {selectedId && <PaymentDetailSheet id={selectedId} onClose={() => setSelectedId(null)} />}
        </SheetContent>
      </Sheet>
    </>
  )
}
